import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, Injector, signal } from '@angular/core';
import { forkJoin, Observable, tap } from 'rxjs';

import { LOADER_INTERCEPTOR_DISABLED_TOKEN } from '@shared/components/loader/loader.interceptor';
import { ConfiguracaoService } from '@shared/configuracao/configuracao.service';
import { CriterioReadService } from '@shared/criterio';
import {
    DataManagerController,
    DataManagerPreferenciaUsuario,
    DataManagerProvider,
} from '@shared/infra/data-manager';
import { PreferenciaUsuarioService } from '@shared/infra/preferencia-usuario/preferencia-usuario.service';
import { WebSocketsService } from '@shared/infra/websockets/websockets.service';
import {
    DashboardConfigModel,
    DashboardPreferenciaCardOrigemModel,
    DashboardPreferenciaUsuarioModel,
} from '../models/dashboard-config.model';
import {
    DashboardCardModel,
    DashboardDadoCardItemModel,
    DashboardDadoModel,
} from '../models/dashboard.model';

@Injectable()
export class DashboardService {
  private readonly apiPath = '/frontend-api/v1/dashboard';

  // Injectables
  private configSvc = inject(ConfiguracaoService);
  private criterioSvc = inject(CriterioReadService);
  private httpClient = inject(HttpClient);
  private injector = inject(Injector);
  private preferenciasUsuarioSvc = inject(PreferenciaUsuarioService);
  private websocketsService = inject(WebSocketsService);

  // Atributos private
  private cardsList = signal<DashboardCardModel[]>([]);
  private dmControllerCache = new Map<
    string,
    DataManagerController<DashboardDadoCardItemModel>
  >();
  private prefUsuario = signal<DashboardPreferenciaUsuarioModel>({
    cards: [],
  });

  constructor() {
    this.reload();
  }

  get cards() {
    return this.cardsList;
  }

  getCard(id: string) {
    const path = `${this.apiPath}/card/${id}`;
    const context = new HttpContext().set(
      LOADER_INTERCEPTOR_DISABLED_TOKEN,
      true
    );
    return this.httpClient.get<DashboardCardModel>(path, { context });
  }

  getDmController(
    idCard: string,
    provider: DataManagerProvider<DashboardDadoCardItemModel>
  ) {
    let dmController = this.dmControllerCache.get(idCard);
    if (dmController) {
      return dmController;
    }

    dmController = new DataManagerController<DashboardDadoCardItemModel>(
      'dashboard-card',
      this.injector,
      provider
    );
    this.dmControllerCache.set(idCard, dmController);
    return dmController;
  }

  /** Aguarda o cálculo dos dados serem finalizados e retorna  */
  getDadosAsync(idCard: string): Observable<DashboardDadoModel> {
    return new Observable<DashboardDadoModel>((subcriber) => {
      const socket = this.websocketsService.getSocket();
      socket.on('connect', () => {
        socket.on('dashboard-card-dados::response', (dados) => {
          subcriber.next(dados);
          socket.disconnect();
          subcriber.complete();
        });
        socket.on('disconnect', () => {
          subcriber.complete();
        });
        socket.on('error', (error) => {
          subcriber.error(error);
          subcriber.complete();
        });
      });

      socket.emit('dashboard-card-dados::request', { idCard });
    });
  }

  getCriterios() {
    return this.criterioSvc.getLista();
  }

  getPreferenciaUsuario() {
    return this.prefUsuario.asReadonly();
  }

  createCard(card: Omit<DashboardCardModel, 'id'>) {
    const path = `${this.apiPath}/card`;
    return this.httpClient.post<DashboardCardModel>(path, card).pipe(
      tap(() => {
        this.reload();
      })
    );
  }

  deleteCard(id: string) {
    const path = `${this.apiPath}/card/${id}`;
    return this.httpClient.delete<DashboardCardModel>(path).pipe(
      tap(() => {
        this.reload();
      })
    );
  }

  getDashboardConfig() {
    return this.configSvc.getConfig<DashboardConfigModel>('dashboard');
  }

  ocultarCard(cardId: string) {
    const prefUsuario = this.prefUsuario();
    const index = prefUsuario.cards.findIndex((c) => c.id === cardId);
    if (index !== -1) {
      prefUsuario.cards.splice(index, 1);
      this.savePreferenciaUsuario(prefUsuario);
    }
  }

  savePreferenciaUsuario(prefUsuario: DashboardPreferenciaUsuarioModel) {
    this.prefUsuario.set(prefUsuario);
    this.preferenciasUsuarioSvc.set('dashboard', prefUsuario).subscribe();
  }

  updatePreferenciaUsuarioCard(
    cardId: string,
    prefUsuarioCard?: DataManagerPreferenciaUsuario,
    origem?: DashboardPreferenciaCardOrigemModel
  ) {
    const prefUsuario = this.prefUsuario();
    const card = prefUsuario.cards.find((c) => c.id === cardId);
    if (card) {
      if (prefUsuarioCard) {
        card.prefUsuario = prefUsuarioCard;
      }
      if (origem) {
        card.origem = origem;
      }
      this.savePreferenciaUsuario(prefUsuario);
    }
  }

  // Métodos private
  private reload() {
    const path = `${this.apiPath}/card`;
    forkJoin([
      this.httpClient.get<DashboardCardModel[]>(path),
      this.preferenciasUsuarioSvc.get<DashboardPreferenciaUsuarioModel>(
        'dashboard'
      ),
    ]).subscribe({
      next: ([cards, prefUsuario]) => {
        this.cardsList.set(cards);

        // Filtra apenas cards existentes
        const cardsOk = prefUsuario?.cards.filter((puc) =>
          cards.find((c) => c.id === puc.id)
        );

        this.prefUsuario.set({ cards: cardsOk || [] });
      },
    });
  }
}
